// src/components/Carousel.js
import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import axios from 'axios';

const Carousel = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);

    const fallbackSlides = [
        {
            id: 1,
            title: 'Добредојдовте во Нашиот Фитнес Центар',
            description: 'Започнете го вашето здравствено патување со нас',
            gradient: 'linear-gradient(135deg, #ff7eb9, #a5d8ff)'
        },
        {
            id:  2,
            title: 'Персонални Тренерски Услуги',
            description: 'Искусни тренери за вашето подобрување',
            gradient:  'linear-gradient(135deg, #a5d8ff, #ff7eb9)'
        },
        {
            id: 3,
            title: 'Модерна Опрема',
            description:  'Најновата фитнес опрема за оптимални резултати',
            gradient: 'linear-gradient(135deg, #c8f0cc, #667eea)'
        }
    ];

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/public/front-page/images`);

                if (res.data && res.data.length > 0) {
                    const sortedImages = res.data. sort((a, b) => a.position - b. position);
                    setSlides(sortedImages. map(img => ({
                        id: img.id,
                        imageUrl: img.imageUrl.startsWith('http') ? img.imageUrl : `${process.env.REACT_APP_API_URL}${img.imageUrl}`,
                        position: img. position
                    })));
                } else {
                    setSlides(fallbackSlides);
                }
            } catch (err) {
                console.error('Error fetching carousel images:', err);
                setSlides(fallbackSlides);
            } finally {
                setLoading(false);
            }
        };

        fetchSlides();
    }, []);

    useEffect(() => {
        if (slides.length === 0) return;

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);

        return () => clearInterval(timer);
    }, [slides.length]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height:  { xs: 200, md: 300 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 3
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (slides.length === 0) {
        return null;
    }

    const hasImages = slides[0]?.imageUrl;

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 200, md: 300 },
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
            <Box sx={{
                display:  'flex',
                width: `${slides. length * 100}%`,
                height: '100%',
                transform: `translateX(-${currentSlide * (100 / slides.length)}%)`,
                transition: 'transform 0.5s ease-in-out'
            }}>
                {slides.map((slide, index) => (
                    <Box
                        key={slide.id || index}
                        sx={{
                            width: `${100 / slides.length}%`,
                            height:  '100%',
                            position: 'relative'
                        }}
                    >
                        {hasImages ?  (
                            <Box
                                component="img"
                                src={slide. imageUrl}
                                alt={`Банер ${slide.position}`}
                                sx={{
                                    width:  '100%',
                                    height:  '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width:  '100%',
                                    height:  '100%',
                                    background: slide.gradient,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: 'white',
                                    textAlign: 'center',
                                    px: 4
                                }}
                            >
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight:  'bold',
                                        fontSize:  { xs: '1.5rem', md:  '2.5rem' }
                                    }}
                                >
                                    {slide.title}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        opacity: 0.9,
                                        fontSize: { xs:  '1rem', md: '1.25rem' }
                                    }}
                                >
                                    {slide.description}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            <IconButton
                onClick={prevSlide}
                sx={{
                    position: 'absolute',
                    left:  16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#ff7eb9',
                    '&:hover': {
                        backgroundColor:  'white',
                        transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                <ChevronLeft />
            </IconButton>
            <IconButton
                onClick={nextSlide}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top:  '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#ff7eb9',
                    '&: hover': {
                        backgroundColor: 'white',
                        transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                <ChevronRight />
            </IconButton>

            <Box sx={{
                position: 'absolute',
                bottom: 16,
                left:  '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1
            }}>
                {slides.map((_, index) => (
                    <Box
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: currentSlide === index ?  'white' : 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            transition:  'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            '&:hover': {
                                backgroundColor: 'white',
                                transform: 'scale(1.2)'
                            }
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default Carousel;